<template>
  <div v-if="warrantyCase" class="products-list" data-cy="product-list">
    <CaseHeader :case-order="warrantyCase" :case-not-submitted="isEditMode" />
    <div
      v-for="line in warrantyCase.lines"
      :key="line.id"
      class="product"
      :data-cy="'product ' + line.productMainGroup">
      <WarrantyCaseLine
        :line="line"
        :case-id="warrantyCase.id"
        :is-editable="props.isEditMode"
        :is-line-delete-enabled="warrantyCase.lines.length > 1"
        :defect-reasons="defectReasonsOptions[line.productMainGroup] || {}"
        :running-distances="runningDistances[line.productMainGroup] || {}"
        :factory-codes="factoryCodes[line.productMainGroup] || {}"
        :production-codes="productionCodes[line.productMainGroup] || {}"
        @form-is-valid="(isValid) => updateCaseValidity(line.id, isValid)"
        @update-line="(updatedLine) => onUpdateLine(updatedLine)"
        @delete-line="(lineToDelete) => onDeleteLine(lineToDelete)" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { CaseType } from '@onrunning/backstage-models';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import CaseHeader from '@/components/cases/CaseHeader.vue';
import WarrantyCaseLine from '@/components/cases/WarrantyCaseLine.vue';
import { useFormat } from '@/composables/format';
import { useNotification } from '@/composables/useNotification';
import type { CaseLine } from '@/models/case-line.model';
import { CasesService } from '@/services/cases.service';
import { useUserStore } from '@/stores/user';
import type { WarrantyCase } from '@/types/case.interface';

const props = defineProps<{
  isEditMode: boolean;
  warrantyCase: WarrantyCase;
}>();

const emit = defineEmits<{
  'update-case': [returnCase: WarrantyCase];
  'delete-case-line': [line: CaseLine];
  'form-is-valid': [isValid: boolean];
}>();

// Services
const casesService = new CasesService();

const userStore = useUserStore();

const { t } = useI18n();
const { notifyError } = useNotification();
const { getTranslationFromTranslateField } = useFormat();

const deleting = ref(false);
const lineValidities = ref<Map<string, boolean>>(new Map());
const runningDistances = ref<Record<string, Record<string, string>>>({});
const defectReasonsOptions = ref<Record<string, Record<string, string>>>({});
const productionCodes = ref<Record<string, Record<string, string>>>({});
const factoryCodes = ref<Record<string, Record<string, string>>>({});

const formIsValid = computed(() => {
  let isValid = true;

  props.warrantyCase.lines.forEach((line) => {
    const isLineValid = !!lineValidities.value.get(line.id);
    if (!isLineValid) {
      isValid = false;
    }
  });

  return isValid;
});

watch(
  () => formIsValid.value,
  (isValid: boolean) => {
    emit('form-is-valid', isValid);
  },
  { immediate: true },
);

onMounted(() => {
  init();
});

const init = async () => {
  if (props.warrantyCase) {
    await Promise.all([
      fetchDefectReasons(props.warrantyCase.type),
      fetchFactoryCodes(),
      fetchProductionCodes(),
      fetchWarrantyRunningDistances(),
    ]);
  }
};

const groupOptions = (items: any[]) => {
  return items.reduce(
    (acc, item) => {
      const desc = getTranslationFromTranslateField(item.description);
      item.productMainGroups.forEach((group: string) => {
        acc[group] = acc[group] ?? {};
        acc[group][item.type] = desc;
      });

      return acc;
    },
    {} as Record<string, Record<string, string>>,
  );
};

const fetchDefectReasons = async (caseType: CaseType) => {
  const result = await casesService.getDefectReasons(caseType, userStore.selectedAccount?.billingCountry);

  if (result.model?.length) {
    defectReasonsOptions.value = groupOptions(result.model);
  }
};

const fetchWarrantyRunningDistances = async () => {
  const result = await casesService.getWarrantyRunningDistances();

  if (result.model?.length) {
    runningDistances.value = groupOptions(result.model);
  }
};

const fetchFactoryCodes = async () => {
  const result = await casesService.getFactoryCodes();

  if (result.model?.length) {
    factoryCodes.value = groupOptions(result.model);
  }
};

const fetchProductionCodes = async () => {
  const result = await casesService.getProductionCodes();

  if (result.model?.length) {
    productionCodes.value = groupOptions(result.model);
  }
};

const onDeleteLine = async (line: CaseLine) => {
  deleting.value = true;
  try {
    const res = await casesService.deleteLine(props.warrantyCase.id, line, userStore.selectedAccount.id);

    if (res.isValid()) {
      emit('delete-case-line', line);
    }
  } catch (_) {
    notifyError(t('backend.cases.line.delete_line_error'));
  } finally {
    deleting.value = false;
  }
};

const onUpdateLine = (updatedLine: CaseLine) => {
  const updatedLines = props.warrantyCase.lines.map((line) => (line.id === updatedLine.id ? updatedLine : line));
  emit('update-case', { ...props.warrantyCase, lines: updatedLines });
};

const updateCaseValidity = (lineId: string, isValid: boolean) => {
  lineValidities.value.set(lineId, isValid);
};
</script>

<style scoped lang="scss">
.products-list {
  padding: 1.25rem;
  background-color: var(--primary-bg-color);
  border-radius: 0.25rem 0.25rem 0 0;
  overflow: visible;

  .product {
    background-color: var(--primary-bg-color);
    margin-bottom: 0.0625rem;

    .product-details {
      max-height: 0;
      overflow: hidden;
      transition:
        max-height 0.5s ease,
        padding 0.5s ease;
    }

    &:last-child {
      border-radius: 0 0 0.25rem 0.25rem;
    }
  }
}
</style>
